import { Box, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { CampaignCreationSection } from 'pages/CampaignCreation/components/CampaignCreationSection';
import { MessagesList } from 'services/conversations/components/MessagesList';
import { useRecoilState } from 'recoil';
import {
  simulationIdAtom,
  simulationMessagesAtom
} from 'services/campaign/atoms';
import { convertToDeprecatedSender } from 'utils/convertToGeneratedSender';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SendMessageInput } from 'components/Inputs/SendMessageInput';
import { useFormik } from 'formik';
import { Sender, TemplateValuesInput } from 'generated-graphql/types';
import { useSendSimulationMessageMutation } from 'generated-graphql/hooks/apollo-hooks';
import { convertToGeneratedQuestionType } from 'utils/convertToGeneratedQuestionType';
import { convertToGeneratedClosingMethod } from 'utils/convertToGeneratedClosingMethod';
// TODO: Change this type
import { ClosingMethod } from 'services/campaign/types';
// TODO: Change this type
import { Tone } from 'services/agents/types';
import { ToastType, useToast } from 'services/toast';
import { convertToGeneratedAgentTone } from 'utils/convertToGeneratedAgentTone';
import { fillTemplate } from 'services/whatsaapTemplates/utils';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { fillOpeningMessageTemplate } from 'core/utils/fillOpeningMessageTemplate';
import { customTemplatedMessageExampleVariables } from 'pages/CampaignCreation/constants';
// TODO: Change this type
import { WhatsaapTemplate } from 'services/whatsaapTemplates/types';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';

const textFieldContainer: SxProps = {
  minWidth: 400,
  width: '100%',
  mt: 2
};

const resetContainerSx: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end'
};

const resetButtonSx: SxProps = {
  px: 0
};

export type CampaignCreationSimulationProps = {
  template: WhatsaapTemplate | null;
  customTemplatedMessage: string | null;
  closingMethod: ClosingMethod;
  customRequest: string | null;
  rejectionMessage: string | null;
  closingMessage: string | null;
  agentName: string;
  agentTone: Tone | null;
};

export const CampaignCreationSimulation: React.FC<
  CampaignCreationSimulationProps
> = ({
  template,
  customTemplatedMessage,
  closingMethod,
  customRequest,
  rejectionMessage,
  closingMessage,
  agentName,
  agentTone
}) => {
  const campaignType = useCampaignCreationStore(
    (store) => store.state?.campaignType
  );
  const jobDescriptionParsed = useCampaignCreationStore(
    (store) => store.state?.jobDescriptionParsedState?.jobDescriptionParsed
  );
  const noATSJobDescriptionState = useCampaignCreationStore(
    (store) => store.state?.noATSJobDescriptionState
  );
  const ATSJobDescriptionState = useCampaignCreationStore(
    (store) => store.state?.ATSJobDescriptionState
  );
  const jobDescriptionText =
    noATSJobDescriptionState?.jobDescriptionText ||
    ATSJobDescriptionState?.selectedJob?.jobDescriptionText ||
    '';
  const language = useCampaignCreationStore((store) => store.state?.language);
  const additionalContext = useCampaignCreationStore(
    (store) => store.state?.jobDescriptionParsedState?.additionalContext
  );
  const questions = useCampaignCreationStore((store) => store.state?.questions);
  const outreachDescription = useCampaignCreationStore(
    (store) => store.state?.engagementDescriptionState?.outreachDescription
  );
  const organization = useOrganization();
  const [simulationId, setSimulationId] = useRecoilState(simulationIdAtom);
  const [messages, setMessages] = useRecoilState(simulationMessagesAtom);
  const messagesRef = useRef(messages);
  const [isLoading, setIsLoading] = useState(false);

  const [sendSimulationMessage] = useSendSimulationMessageMutation();
  const { showToast } = useToast();

  const agentToneGenerated = convertToGeneratedAgentTone(agentTone);

  const formik = useFormik({
    initialValues: { messageBody: '' },
    onSubmit: async (values) => {
      const { messageBody } = values;

      try {
        if (closingMethod && campaignType) {
          setIsLoading(true);

          const messageSent = {
            id: Date.now().toString(),
            body: messageBody,
            createdAt: new Date().toISOString(),
            sender: Sender.Prospect,
            simulationId: simulationId || '',
            userId: '',
            organizationId: organization?.id || '',
            updatedAt: new Date().toISOString()
          };

          messagesRef.current = [...messagesRef.current, messageSent];
          setMessages(messagesRef.current);
          formik.resetForm();

          const { data } = await sendSimulationMessage({
            variables: {
              input: {
                messageBody,
                jobDescriptionParsed,
                jobDescriptionText,
                additionalContext,
                agentName,
                questions: (questions || []).map((question, i) => ({
                  content: question.content,
                  isMandatory: question.isMandatory,
                  questionType: convertToGeneratedQuestionType(
                    question.questionType
                  ),
                  expectedAnswer: question.expectedAnswer,
                  order: i,
                  documentTypes: question.documentTypes,
                  preferredAnswerText: question.preferredAnswerText,
                  hasPreferredAnswer: question.hasPreferredAnswer,
                  preferredAnswerRequirements:
                    question.preferredAnswerRequirements || []
                })),
                openingMessage: messages[0].body,
                simulationId: simulationId || undefined,
                closingMethod: convertToGeneratedClosingMethod(
                  closingMethod || undefined
                ),
                customRequest,
                closingMessage,
                type: campaignType,
                tone: agentToneGenerated,
                rejectionMessage,
                outreachDescription,
                language: language || undefined
              }
            }
          });

          if (!data?.sendSimulationMessage) {
            return;
          }

          const {
            message: messageReceived,
            simulationId: simulationIdReceived
          } = data.sendSimulationMessage;

          if (!simulationId) {
            setSimulationId(simulationIdReceived);
          }

          if (messageReceived) {
            messagesRef.current = [...messagesRef.current, messageReceived];
            setMessages(messagesRef.current);
          }
        }
      } catch (e) {
        showToast({
          message: 'Could not process message. Please try again.',
          type: ToastType.Error
        });
      } finally {
        setIsLoading(false);
      }
    }
  });

  const setTestInitialMessage = useCallback(
    (body: string) => {
      messagesRef.current = [
        {
          id: Date.now().toString(),
          body,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          sender: Sender.Agent,
          simulationId: '',
          userId: '',
          organizationId: organization?.id || ''
        }
      ];

      setMessages(messagesRef.current);

      if (simulationId) {
        setSimulationId('');
      }
    },
    [setMessages, setSimulationId, simulationId]
  );

  const templateValues = useMemo<TemplateValuesInput>(
    () => ({
      firstName: 'John',
      agentName,
      companyName: organization?.name || '',
      jobTitle: jobDescriptionParsed?.jobTitle || '',
      employerName: jobDescriptionParsed?.employerName || '',
      location: jobDescriptionParsed?.location || '',
      chatUrl: '',
      currentRoleCompanyName: '',
      industry: ''
    }),
    [agentName, jobDescriptionParsed, organization]
  );

  useEffect(() => {
    if (!customTemplatedMessage) {
      if (!template) return;
      setTestInitialMessage(
        fillTemplate(template?.content, template?.mapping, templateValues) || ''
      );
      return;
    }

    setTestInitialMessage(
      fillOpeningMessageTemplate(
        customTemplatedMessage,
        customTemplatedMessageExampleVariables({
          location: jobDescriptionParsed?.location || '',
          jobTitle: jobDescriptionParsed?.jobTitle || '',
          employerName: jobDescriptionParsed?.employerName || '',
          agentName,
          organizationName: organization?.name || ''
        })
      )
    );
  }, [template, customTemplatedMessage, agentName, templateValues]);

  return (
    <CampaignCreationSection title="Simulate" subTitle="Try out your campaign">
      <Box>
        <Box>
          <MessagesList
            messages={messages.map((message) => ({
              ...message,
              sender: convertToDeprecatedSender(message.sender)
            }))}
            agentDisplayName={agentName}
            prospectDisplayName="Candidate"
          />
        </Box>
        <Box sx={resetContainerSx}>
          <Button
            variant="tertiary"
            onClick={() => {
              if (simulationId && messagesRef.current.length > 1) {
                setSimulationId('');
                messagesRef.current = [messagesRef.current[0]];
                setMessages(messagesRef.current);
              }
            }}
            sx={resetButtonSx}
          >
            Reset
          </Button>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={textFieldContainer}>
            <SendMessageInput
              onSend={formik.submitForm}
              label="Type your reply"
              variant="outlined"
              fullWidth
              name="messageBody"
              id="messageBody"
              value={formik.values.messageBody}
              onChange={formik.handleChange}
              isLoading={isLoading}
              placeholder={
                isLoading ? 'Waiting for reply...' : 'Type your reply here...'
              }
              disabled={isLoading}
            />
          </Box>
        </form>
      </Box>
    </CampaignCreationSection>
  );
};
